import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@components/ui/button/Button';
import { Checkbox } from '@shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { CreateEndpointPayload, UpdateEndpointPayload, WebhookEndpoint, WebhookEventType } from '../../models/Webhooks';

interface CreateEndpointDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateEndpointPayload | UpdateEndpointPayload) => void;
  events: WebhookEventType[];
  endpoint?: WebhookEndpoint | null;
  isLoading?: boolean;
}

const schema = z.object({
  description: z.string().trim().min(1),
  url: z.string().trim().url(),
  event_types: z.array(z.string().min(1)).min(1),
});

type FormValues = z.infer<typeof schema>;

const DEFAULT_VALUES: FormValues = { description: '', url: '', event_types: [] };

export function CreateEndpointDialog({
  isOpen,
  onClose,
  onSubmit,
  events,
  endpoint = null,
  isLoading,
}: CreateEndpointDialogProps) {
  const { t } = useTranslation('webhooksModule');
  const isEditMode = endpoint != null;

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!isOpen) {
      reset(DEFAULT_VALUES);
      return;
    }

    if (endpoint) {
      reset({
        description: endpoint.description ?? '',
        url: endpoint.url,
        event_types: endpoint.event_types,
      });
      return;
    }

    reset(DEFAULT_VALUES);
  }, [isOpen, endpoint, reset]);

  const selectedCount = watch('event_types').length;

  const eventsByCategory = events.reduce<Record<string, WebhookEventType[]>>((acc, event) => {
    const category = event.category || t('createDialog.uncategorized');
    if (!acc[category]) acc[category] = [];
    acc[category].push(event);
    return acc;
  }, {});

  const submit = handleSubmit((values) => {
    onSubmit(values);
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('editDialog.title') : t('createDialog.title')}</DialogTitle>
          <DialogDescription>
            {isEditMode ? t('editDialog.description') : t('createDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="endpoint-description">{t('createDialog.name')}</Label>
            <Input
              id="endpoint-description"
              placeholder={t('createDialog.namePlaceholder')}
              {...register('description')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="endpoint-url">{t('createDialog.url')}</Label>
            <Input
              id="endpoint-url"
              type="url"
              placeholder={t('createDialog.urlPlaceholder')}
              className="font-mono"
              {...register('url')}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('createDialog.events', { count: selectedCount })}</Label>
            <Controller
              control={control}
              name="event_types"
              render={({ field }) => (
                <div className="border rounded-lg p-4 space-y-4 max-h-64 overflow-y-auto">
                  {Object.entries(eventsByCategory).map(([category, categoryEvents]) => (
                    <div key={category} className="space-y-2">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {category}
                      </h4>
                      <div className="space-y-2">
                        {categoryEvents.map((event) => {
                          const checkboxId = `webhook-event-${event.code}`;
                          const checked = field.value.includes(event.code);
                          const toggle = () => {
                            field.onChange(
                              checked ? field.value.filter((c) => c !== event.code) : [...field.value, event.code],
                            );
                          };
                          return (
                            <div key={event.code} className="flex items-start gap-3 p-2 rounded-md hover:bg-muted">
                              <Checkbox id={checkboxId} checked={checked} onCheckedChange={toggle} />
                              <label htmlFor={checkboxId} className="flex-1 space-y-0.5 cursor-pointer">
                                <p className="text-sm font-medium">{event.description}</p>
                                <p className="text-xs text-muted-foreground font-mono">{event.code}</p>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t('createDialog.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={!isValid || isLoading}>
              {isEditMode ? t('editDialog.submit') : t('createDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
